<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid" :columnDefs="columnDefs" :rowData="rowData" :isAutoWidth="true">
            <date-range-picker
                key="guest-reviews-date-range-picker"
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="onDateRangeChange"
            />
            <b-button-group class="mr-1">
                <search-multiselect
                    :icon="['fas', 'hotel']"
                    v-model="selectedProperties"
                    :options="properties"
                    variant="primary"
                    itemLabel="propertie"
                    placeholder="Properties"
                    controlValue="Properties"
                    @input="fetchData"
                ></search-multiselect>
            </b-button-group>
            <responsive-dropdown :icon="['fas', 'filter']" :items="formTypeFilterOptions" variant="primary">
                {{ formTypeFilterLabel }}
            </responsive-dropdown>
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import api from '../../api';
import { HOSTED_FORM_RESERVATION_ENDPOINT, HOSTED_FORM_ENDPOINT } from '../../endpoints';
import { formatResponseError } from '../../utils/api';
import grid from '../generic/grid/grid.vue';
import Page from '../generic/page/page.vue';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    LAST_WEEK,
    THIS_WEEK,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    FUTURE,
} from '../generic/date-range-picker/date-range-picker.vue';
import searchMultiselect from '../generic/search-multiselect/search-multiselect.vue';
import responsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
export default {
    components: { grid, Page, DateRangePicker, responsiveDropdown, searchMultiselect },
    data() {
        return {
            rowData: [],
            busy: false,
            dateRequestFormat: 'yyyy-MM-dd',
            dateRange: nameToRange(THIS_YEAR),
            selectedProperties: [],
            predefinedRanges: [
                FUTURE,
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            properties: [],
            formTypeFilter: '',
            formTypeFilterLabel: 'Show All Forms',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        tabs() {
            return [
                {
                    title: 'Hosted Form List',
                    route: '/app/hosted-form/list/',
                    active: this.$route.path === '/app/hosted-form/list/',
                },
                {
                    title: 'Requested Forms',
                    route: '/app/hosted-form/pending/',
                    active: this.$route.path === '/app/hosted-form/pending/',
                },
                {
                    title: 'Сompleted Forms',
                    route: '/app/hosted-form/completed/',
                    active: this.$route.path === '/app/hosted-form/completed/',
                },
            ];
        },
        columnDefs() {
            return [
                {
                    field: 'created',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'meta_reservation',
                    headerName: 'Invoice#',
                    cellRenderer: ({ data }) => {
                        return `<a href="/app/reservation/edit/invoice/${data.meta_reservation}">${data.meta_reservation}</a>`;
                    },
                },
                {
                    field: 'property',
                    headerName: 'Property',
                },
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                },
                {
                    field: 'guest_email',
                    headerName: 'Guest Email',
                },
                {
                    field: 'form_title',
                    headerName: 'Form',
                    cellRenderer: ({ data }) => {
                        return `<a href="/app/hosted-form/${data.hosted_form}/edit/">${data.form_title}</a>`;
                    },
                },
                {
                    field: 'submitted_by',
                    headerName: 'Submitted By',
                    autoWidthExclude: true,
                },
                {
                    field: '',
                    headerName: 'Data',
                    cellRenderer: ({ data }) => `<a href="/hosted-form/results/${data.uuid}" target="_blank">View</a>`,
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        formTypeFilterOptions() {
            return [
                {
                    label: 'Show All Forms',
                    click: () => {
                        this.formTypeFilter = '';
                        this.formTypeFilterLabel = 'Show All Forms';
                        this.fetchData();
                    },
                },
                {
                    label: 'Show Housekeeper Checklists',
                    click: () => {
                        this.formTypeFilter = 'HOUSEKEEPER';
                        this.formTypeFilterLabel = 'Show Housekeeper Checklists';
                        this.fetchData();
                    },
                },
                {
                    label: 'Show Guest Forms',
                    click: () => {
                        this.formTypeFilter = 'GUEST';
                        this.formTypeFilterLabel = 'Show Guest Forms';
                        this.fetchData();
                    },
                },
            ];
        },
    },
    mounted() {
        this.fetchData();
        this.fetchProperties();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await api.get(HOSTED_FORM_RESERVATION_ENDPOINT() + 'completed/', {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        properties: this.selectedProperties.join(','),
                        type: this.formTypeFilter,
                    },
                });
                this.rowData = data;
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({ text: generic || 'Could not fetch hosted forms' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                const { data } = await api.get(HOSTED_FORM_ENDPOINT() + 'properties/');
                this.properties = data.map((item) => ({
                    value: item.property_id,
                    text: item.name,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not load properties' });
            }
        },
        onDateRangeChange(range) {
            this.dateRange = range;
            this.fetchData();
        },
    },
};
</script>

<style>
</style>